import { Plus, Search } from "lucide-react";

/**
 * Shared header + toolbar shell for admin resource pages
 * (Contacts, Bills, Campaigns, Events, Statements, and future: Tasks, Legislators, Parishes).
 *
 * Layout: eyebrow + serif title on the left; optional search input, optional toolbar slot,
 * optional "New" button on the right. Everything below the header is `children`.
 *
 * Props:
 *   eyebrow, title       — header text
 *   testId               — top-level data-testid (also used to derive the new-button testid)
 *   search               — { value, onChange, placeholder?, testId? } → renders a search input
 *   toolbar              — arbitrary node placed between search and the New button
 *   onNew, newLabel      — creates the New button when onNew is provided
 *   newButtonTestId      — override the auto-derived testid on the New button
 */
export default function ResourceList({
  eyebrow, title, testId,
  search,
  toolbar,
  onNew, newLabel = "New", newButtonTestId,
  children,
}) {
  return (
    <div data-testid={testId}>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          {eyebrow && <div className="text-xs uppercase tracking-wider text-ccc-purple">{eyebrow}</div>}
          <h1 className="font-serif text-4xl text-ccc-plum">{title}</h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {search && (
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
              <input
                value={search.value}
                onChange={e => search.onChange(e.target.value)}
                placeholder={search.placeholder || "Search..."}
                data-testid={search.testId}
                className="pl-9 pr-4 py-2 border border-stone-300 bg-white w-72 focus:outline-none focus:border-ccc-purple"
              />
            </div>
          )}
          {toolbar}
          {onNew && (
            <button
              onClick={onNew}
              data-testid={newButtonTestId || `${testId}-new`}
              className="bg-ccc-purple hover:bg-ccc-purple-dark text-white px-4 py-2 inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold"
            >
              <Plus size={14}/> {newLabel}
            </button>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
